import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { useCodatAuth } from "@codat/client-cookie-manager";
import {
  Button,
  ErrorDialog,
  OrchardProvider,
  Tab,
  TabPanel,
  Tabs,
} from "@codat/orchard-ui";
import {
  CodatClientInvitation,
  useGetCodatClientInvitations,
} from "api/clientInvites";
import {
  CodatError,
  CodatUser,
  UserRole,
  useUserRolesQuery,
  useUsersQuery,
} from "api/users";
import {
  PermissionsContextProvider,
  PermissionsStatusType,
  usePermissionsContext,
} from "contexts/PermissionsContext";
import { useEffect, useState } from "react";

import Page from "components/Page";
import PageHeader from "components/PageHeader";
import AddUserModal from "pages/UserManagement/AddUserModal";
import DeleteInviteModal from "pages/UserManagement/DeleteInviteModal";
import DeleteUserModal from "pages/UserManagement/DeleteUserModal";
import EditUserModal from "pages/UserManagement/EditUserModal";
import InvitesTable from "pages/UserManagement/InvitesTable";
import ManageLoginModal from "pages/UserManagement/ManageLoginModal";
import ResendInviteModal from "pages/UserManagement/ResendInviteModal";
import UsersTable from "pages/UserManagement/UsersTable";

import { getAuth0idpFromToken, GetClientId } from "../../api/setup";
import styles from "./UserManagement.module.scss";
import {
  CreateUserManagementService,
  UserManagementServiceContext,
} from "./UserManagementService";

const usersTab = "Users Tab";
const invitesTab = "Invites Tab";

export function isOnlyAdmin(currentUser: CodatUser, users: CodatUser[]) {
  const adminCount = users.filter(
    (user) => user.role === "Administrator"
  ).length;

  if (currentUser.role === "Administrator" && adminCount === 1) {
    return true;
  }

  return false;
}

interface UserManagementPageProps {
  users?: CodatUser[];
  invitations?: CodatClientInvitation[];
  permissions?: string[];
  userRoleOptions?: UserRole[];
  pageUsers: number;
  pageSizeUsers: number;
  pageInvites: number;
  pageSizeInvites: number;
  onPageUpdateUsers: (page: number) => void;
  onPageSizeUpdateUsers: (page: number) => void;
  onPageUpdateInvites: (page: number) => void;
  onPageSizeUpdateInvites: (page: number) => void;
  totalResultsUser?: number;
  totalResultsInvites?: number;
  isSkeleton: boolean;
  getUsers: () => void;
  getInvites: () => void;
}

export const UserManagementPageContent = ({
  users = [],
  invitations = [],
  permissions = [],
  userRoleOptions = [],
  pageUsers,
  pageSizeUsers,
  pageInvites,
  pageSizeInvites,
  onPageUpdateUsers,
  onPageSizeUpdateUsers,
  onPageUpdateInvites,
  onPageSizeUpdateInvites,
  totalResultsUser = 0,
  totalResultsInvites = 0,
  isSkeleton,
  getUsers,
  getInvites,
}: UserManagementPageProps) => {
  const { sendEvent } = useAnalytics();
  const [addUserModal, setAddUserModal] = useState(false);
  const [manageLoginModal, setManageLoginModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState<{
    open: boolean;
    user?: CodatUser;
  }>({
    open: false,
  });

  const [selectedTabId, setSelectedTabId] = useState("tab-users");

  const handleTabChange = (value: string) => {
    setSelectedTabId(value);
    sendEvent("Tab - Change", { tab: value });
  };

  const [resendInviteModal, setResendInviteModal] = useState<{
    open: boolean;
    invite?: CodatClientInvitation;
  }>({
    open: false,
  });

  const [deleteInviteModal, setDeleteInviteModal] = useState<{
    open: boolean;
    invite?: CodatClientInvitation;
  }>({
    open: false,
  });

  const [deleteUserModal, setDeleteUserModal] = useState<{
    open: boolean;
    user?: CodatUser;
  }>({
    open: false,
  });

  const handleInviteResend = () => {
    getInvites();
    setResendInviteModal({ open: false });
  };

  const handleInviteDelete = () => {
    getInvites();
    setDeleteInviteModal({ open: false });
  };

  const handleUserAdded = () => {
    getUsers();
    getInvites();
    setAddUserModal(false);
  };

  const handleUserUpdated = (updatedUser: CodatUser) => {
    if (users) {
      const index = users.findIndex((u) => u.id === updatedUser.id);
      if (index > -1) {
        users[index] = updatedUser;
      }
    }

    setEditUserModal({ open: false });
  };

  const handleUserDeleted = () => {
    getUsers();
    setDeleteUserModal({ open: false });
  };

  const { authType, userData } = useCodatAuth();

  const token = userData?.access_token;
  const isSsoUser =
    authType === "Auth0" && getAuth0idpFromToken(token) === "enterprisesso";

  return (
    <div data-testid="user-management-content">
      <PageHeader header="Manage users" underline>
        <div className={styles.headerButtons}>
          {!isSkeleton && permissions?.includes("ManageLogin") && (
            <Button
              data-testid="manage-login"
              label="Manage sign-in"
              onClick={() => {
                setManageLoginModal(true);
                sendEvent("Manage Sign-in Button - Click");
              }}
            />
          )}
          {!isSkeleton && permissions?.includes("Add") && (
            <Button
              data-testid="add-user"
              label="Invite user"
              onClick={() => {
                setAddUserModal(true);
                sendEvent("Invite User Button - Click");
              }}
              disabled={isSsoUser}
            />
          )}
        </div>
      </PageHeader>
      <Tabs
        label="example-tabs"
        selectedTabId={selectedTabId}
        onTabChange={(_, tab) => handleTabChange(tab)}
      >
        <Tab
          id="tab-users"
          data-testid="tab-users"
          aria-controls="tabpanel-tab-users"
          label="Users"
        />
        <Tab
          id="tab-invites"
          data-testid="tab-invites"
          aria-controls="tabpanel-tab-invites"
          label="Invites"
        />
      </Tabs>

      <TabPanel tabId="tab-users" selectedTabId={selectedTabId}>
        <EventContextProvider name={usersTab}>
          <UsersTable
            users={users}
            page={pageUsers}
            setPage={onPageUpdateUsers}
            pageSize={pageSizeUsers}
            setPageSize={onPageSizeUpdateUsers}
            totalResults={totalResultsUser}
            isSkeleton={isSkeleton}
            onEditUser={(user) => setEditUserModal({ open: true, user })}
            onDeleteUser={(user) => setDeleteUserModal({ open: true, user })}
            disableInteractions={isSsoUser}
          />
        </EventContextProvider>
      </TabPanel>
      <TabPanel tabId="tab-invites" selectedTabId={selectedTabId}>
        <EventContextProvider name={invitesTab}>
          <InvitesTable
            invitations={invitations}
            page={pageInvites}
            setPage={onPageUpdateInvites}
            pageSize={pageSizeInvites}
            setPageSize={onPageSizeUpdateInvites}
            totalResults={totalResultsInvites}
            isSkeleton={isSkeleton}
            onResendInvite={(invite) => {
              setResendInviteModal({ open: true, invite });
            }}
            onDeleteInvite={(invite) => {
              setDeleteInviteModal({ open: true, invite });
            }}
            disableInteractions={isSsoUser}
          />
        </EventContextProvider>
      </TabPanel>

      <EventContextProvider name="Invite User Modal">
        <AddUserModal
          open={addUserModal}
          userRoleOptions={userRoleOptions ?? []}
          onClose={() => setAddUserModal(false)}
          onSuccess={handleUserAdded}
        />
      </EventContextProvider>
      {editUserModal.user && (
        <EventContextProvider name={`${usersTab} - Edit User Modal`}>
          <EditUserModal
            open={editUserModal.open}
            user={editUserModal.user}
            userRoleOptions={userRoleOptions ?? []}
            onClose={() => setEditUserModal({ open: false })}
            onSuccess={handleUserUpdated}
            isOnlyAdmin={isOnlyAdmin(editUserModal.user, users)}
          />
        </EventContextProvider>
      )}
      {deleteUserModal.user && (
        <EventContextProvider name={`${usersTab} - Delete User Modal`}>
          <DeleteUserModal
            open={deleteUserModal.open}
            user={deleteUserModal.user}
            onCancel={() => setDeleteUserModal({ open: false })}
            onSuccess={handleUserDeleted}
          />
        </EventContextProvider>
      )}
      {resendInviteModal.invite && (
        <EventContextProvider name={`${invitesTab} - Resend Invite Modal`}>
          <ResendInviteModal
            open={resendInviteModal.open}
            invitation={resendInviteModal.invite}
            onCancel={() => setResendInviteModal({ open: false })}
            onSuccess={handleInviteResend}
          />
        </EventContextProvider>
      )}
      {deleteInviteModal.invite && (
        <EventContextProvider name={`${invitesTab} - Delete Invite Modal`}>
          <DeleteInviteModal
            open={deleteInviteModal.open}
            invite={deleteInviteModal.invite}
            onCancel={() => setDeleteInviteModal({ open: false })}
            onSuccess={handleInviteDelete}
          />
        </EventContextProvider>
      )}
      {manageLoginModal && (
        <EventContextProvider name="Manage Sign-in Modal">
          <ManageLoginModal
            open={manageLoginModal}
            onClose={() => setManageLoginModal(false)}
          />
        </EventContextProvider>
      )}
    </div>
  );
};

export const UserManagement = () => {
  const { status, data: permissions } = usePermissionsContext();
  const { sendPageViewEvent } = useAnalytics();

  const [pageUsers, setPageUsers] = useState(1);
  const [pageSizeUsers, setPageSizeUsers] = useState(10);

  const [pageInvites, setPageInvites] = useState(1);
  const [pageSizeInvites, setPageSizeInvites] = useState(10);

  const userManagementService = CreateUserManagementService();

  const [{ error, data: userData }, getUsers] = useUsersQuery({
    page: pageUsers,
    pageSize: pageSizeUsers,
  });

  const [{ error: userRolesError, data: userRoleOptions }] =
    useUserRolesQuery();

  const [{ error: invitesError, data: invitesData }, getInvites] =
    useGetCodatClientInvitations(GetClientId(), {
      page: pageInvites,
      pageSize: pageSizeInvites,
    });

  useEffect(() => {
    sendPageViewEvent("Manage users");
  }, [sendPageViewEvent]);

  const isComplete =
    status === PermissionsStatusType.READY &&
    !!userData?.results &&
    !!invitesData?.results &&
    !!userRoleOptions;

  const loadError = error ?? userRolesError ?? invitesError;

  if (loadError) {
    const codatError = loadError.response?.data as CodatError;
    const errorMessage = codatError?.error
      ? `An error occured getting users: ${codatError?.error}`
      : "An unexpected error occured getting users";

    return (
      <ErrorDialog
        statusCode={codatError?.statusCode ?? loadError.response?.status ?? 500}
        correlationId={codatError?.correlationId}
        message={errorMessage}
      />
    );
  }

  return (
    <EventContextProvider name="Manage Users">
      <Page className={styles.page}>
        <UserManagementServiceContext.Provider value={userManagementService}>
          <UserManagementPageContent
            users={userData?.results}
            invitations={invitesData?.results}
            permissions={permissions}
            userRoleOptions={userRoleOptions}
            pageUsers={pageUsers}
            pageSizeUsers={pageSizeUsers}
            pageInvites={pageInvites}
            pageSizeInvites={pageSizeInvites}
            onPageUpdateUsers={setPageUsers}
            onPageSizeUpdateUsers={setPageSizeUsers}
            onPageUpdateInvites={setPageInvites}
            onPageSizeUpdateInvites={setPageSizeInvites}
            totalResultsUser={userData?.totalResults}
            totalResultsInvites={invitesData?.totalResults}
            isSkeleton={!isComplete}
            getUsers={() => getUsers()}
            getInvites={() => getInvites()}
          />
        </UserManagementServiceContext.Provider>
      </Page>
    </EventContextProvider>
  );
};

export const UserManagementRemote = () => (
  <OrchardProvider appId="user-management">
    <PermissionsContextProvider requiredPermissions={["View"]}>
      <UserManagement />
    </PermissionsContextProvider>
  </OrchardProvider>
);
